import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { useCreateDecisionTree } from "@/queries/decisionTrees";
import { useCapability } from "@/rbac/useCapability";

import { treesBasePath, useAuthoringScope } from "../lib/authoringScope";
import { STARTER_TREE_YAML, applyCodeToYaml } from "../lib/treeStructure";
import { TREE_TEMPLATES, getTemplate } from "../lib/treeTemplates";
import { MutationErrorBanner } from "../components/MutationErrorBanner";
import { TreeTargetingPicker } from "../components/TreeTargetingPicker";
import { Card } from "@/components/Card";
import { Field, FIELD_CONTROL_CLASS } from "@/components/Field";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";

// Default for the YAML textarea when the page first loads. Authors who
// want to start clean can pick "Empty" from the template picker and
// click Start from scratch instead of touching this body at all.
//
// `code:` is REPLACE_ME like every template: the form field owns the code
// and `applyCodeToYaml` writes it in as the author types. It used to be a
// realistic-looking `my_tree_v1`, which nothing rewrote — so the first
// save of a from-scratch tree always died on the backend's code-mismatch
// check with no hint that the form field was the thing to reconcile.
const DEFAULT_YAML = `code: REPLACE_ME
name_en: My new tree
name_ar: شجرتي الجديدة
description_en: One-paragraph what + why.
description_ar: ...

# Targeting (crops / countries / soils / execution scope) is form-driven —
# whatever the pickers above say is written onto the tree when it is saved.

root: root
nodes:
  root:
    label_en: Decision question
    condition:
      tree:
        op: lt
        left: { source: indices, index_code: ndvi, key: baseline_deviation }
        right: -0.5
    on_match: leaf_action
    on_miss:  leaf_no_action

  leaf_action:
    label_en: Suggested action
    outcome:
      action_type: scout
      severity: warning
      confidence: 0.7
      valid_for_hours: 72
      parameters:
        priority: medium
      text_en: Schedule a scouting visit.
      text_ar: حدِّد زيارة فحص.

  leaf_no_action:
    outcome:
      action_type: no_action
      severity: info
      confidence: 0.9
      text_en: No action.
      text_ar: لا إجراء.
`;

// The shape the backend enforces on `code`. The text input carries this as
// an HTML `pattern`, which only guards the submit button — "Start from
// scratch" is a plain button and posted straight past it, so a code with a
// space or a capital in it came back as an opaque 422 instead of the inline
// "match the requested format" the other button gives you. Both paths check
// against this now.
//
// Kept in sync by hand with the `pattern` attribute below: a JS regex and an
// HTML pattern are parsed under different flags, so they can't be one string
// (see the escaping note on the input).
const CODE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export function DecisionTreeCreatePage(): ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation("decisionTrees");
  const canManage = useCapability("decision_tree.manage");
  // Named apart from the tree's block/cell `scope`, which is a different
  // thing entirely and already owns that word on this page.
  const authoringScope = useAuthoringScope();
  const base = treesBasePath(authoringScope);

  const [code, setCode] = useState("");
  const [cropPaths, setCropPaths] = useState<string[]>([]);
  const [countryCodes, setCountryCodes] = useState<string[]>([]);
  const [soilTextures, setSoilTextures] = useState<string[]>([]);
  const [scope, setScope] = useState<"block" | "cell">("block");
  const [yamlBody, setYamlBody] = useState(DEFAULT_YAML);
  // PR-D8: tracking the selected template id lets the dropdown stay
  // in sync after a swap, and powers the "Start from scratch" button
  // which jumps straight to the canvas.
  const [templateId, setTemplateId] = useState<string>("custom");

  const create = useCreateDecisionTree();

  if (!canManage) {
    return <Navigate to={base} replace />;
  }

  // The code field is the source of truth for `code:` in the body, so
  // every keystroke rewrites that one line. Without this the author sees
  // the form and the YAML disagree and the save fails on the backend's
  // code-mismatch check.
  const onCodeChange = (next: string): void => {
    setCode(next);
    setYamlBody((prev) => applyCodeToYaml(prev, next));
  };

  // Swap the YAML body when the user picks a different template. We
  // also reset the template id to "custom" if the author then edits
  // the YAML by hand, so the dropdown reflects the divergence.
  const onTemplateChange = (id: string): void => {
    setTemplateId(id);
    const tpl = getTemplate(id);
    if (tpl) setYamlBody(applyCodeToYaml(tpl.yaml, code));
  };
  const onYamlEdit = (next: string): void => {
    setYamlBody(next);
    setTemplateId("custom");
  };

  // Crop targeting is required (the tree must declare at least one crop).
  const targetingReady = cropPaths.length > 0;
  const trimmedCode = code.trim();
  const codeValid = CODE_PATTERN.test(trimmedCode);
  // Only complain once there is something to complain about — an empty
  // field is "not filled in yet", not "wrong".
  const showCodeError = trimmedCode !== "" && !codeValid;
  const targeting = {
    crop_paths: cropPaths,
    country_codes: countryCodes,
    soil_textures: soilTextures,
    scope,
  };

  const onStartFromScratch = (): void => {
    if (!codeValid || !targetingReady) return;
    const yaml = applyCodeToYaml(STARTER_TREE_YAML, code);
    create.mutate(
      {
        code: trimmedCode,
        tree_yaml: yaml,
        ...targeting,
      },
      {
        onSuccess: (tree) => {
          navigate(`${base}/${tree.code}`);
        },
      },
    );
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!targetingReady || !codeValid) return;
    // Re-apply on submit as well as on keystroke: the author may have
    // hand-edited the `code:` line in the textarea, and the form field is
    // what the backend compares against.
    const yaml = applyCodeToYaml(yamlBody, code);
    create.mutate(
      {
        code: trimmedCode,
        tree_yaml: yaml,
        ...targeting,
      },
      {
        onSuccess: (tree) => {
          navigate(`${base}/${tree.code}`);
        },
      },
    );
  };

  return (
    <Page>
      {/* Forms keep their own measure — see the note in Page.tsx. */}
      <form onSubmit={submit} className="flex max-w-3xl flex-col gap-4">
        <header>
          <PageHeader title={t("create.title")} subtitle={t("create.subtitle")} />
        </header>

        <Card noPadding className="grid grid-cols-1 gap-3 p-4">
          <Field label={t("create.fields.code")} help={t("create.fields.codeHint")} required>
            {(props) => (
              <input
                {...props}
                required
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                placeholder={t("create.fields.codePlaceholder")}
                // The hyphen is escaped because Chrome parses the `pattern`
                // attribute with the `v` flag, where a bare `-` inside a class is
                // a syntax error. An uncompilable pattern is *ignored*, so this
                // silently validated nothing and logged on every page load.
                pattern="^[a-z0-9][a-z0-9_\-]*$"
                aria-invalid={showCodeError || undefined}
                className={`${FIELD_CONTROL_CLASS} sm:max-w-sm`}
              />
            )}
          </Field>
          {showCodeError ? (
            <p role="alert" className="text-xs text-ap-crit">
              {t("create.fields.codeInvalid")}
            </p>
          ) : null}
        </Card>

        <Card noPadding className="flex flex-col gap-2 p-4">
          <div>
            <h2 className="text-sm font-semibold text-ap-ink">{t("targeting.heading")}</h2>
            <p className="text-xs text-ap-muted">{t("targeting.subtitle")}</p>
          </div>
          <TreeTargetingPicker
            cropPaths={cropPaths}
            countryCodes={countryCodes}
            soilTextures={soilTextures}
            onCropPathsChange={setCropPaths}
            onCountryCodesChange={setCountryCodes}
            onSoilTexturesChange={setSoilTextures}
            cropRequired
          />
          <div className="mt-2 flex flex-col gap-1.5 border-t border-ap-line pt-3">
            <span className="text-xs font-medium text-ap-muted">{t("targeting.scope")}</span>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(["block", "cell"] as const).map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-xs text-ap-ink">
                  <input
                    type="radio"
                    name="tree-scope"
                    value={s}
                    checked={scope === s}
                    onChange={() => setScope(s)}
                  />
                  {t(`targeting.scopeValues.${s}`)}
                </label>
              ))}
            </div>
            <span className="text-[11px] text-ap-muted">{t("targeting.scopeHint")}</span>
          </div>
        </Card>

        <Card noPadding className="flex flex-col gap-2 p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-ap-ink">{t("create.fields.yaml")}</h2>
              <p className="text-xs text-ap-muted">{t("create.fields.yamlHint")}</p>
            </div>
            <Field label={t("create.templates.heading")}>
              {(props) => (
                <select
                  {...props}
                  value={templateId}
                  onChange={(e) => onTemplateChange(e.target.value)}
                  className={FIELD_CONTROL_CLASS}
                >
                  <option value="custom">{t("create.templates.custom")}</option>
                  {TREE_TEMPLATES.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {t(tpl.labelKey)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {templateId !== "custom" ? (
            <p className="text-xs text-ap-muted">
              {t(getTemplate(templateId)?.descKey ?? "create.templates.customDesc")}
            </p>
          ) : null}
          <textarea
            value={yamlBody}
            onChange={(e) => onYamlEdit(e.target.value)}
            rows={28}
            spellCheck={false}
            className="w-full rounded-md border border-ap-line bg-ap-bg/40 px-3 py-2 font-mono text-xs text-ap-ink shadow-inner focus:border-ap-primary focus:outline-none focus:ring-1 focus:ring-ap-primary"
          />
        </Card>

        {create.isError ? (
          <MutationErrorBanner error={create.error} fallback={t("create.saveFailed")} />
        ) : null}

        <footer className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(base)}
            className="rounded-md border border-ap-line bg-ap-panel px-3 py-1.5 text-sm font-medium text-ap-ink hover:bg-ap-line/40"
          >
            {t("create.cancel")}
          </button>
          <button
            type="button"
            onClick={onStartFromScratch}
            disabled={create.isPending || !codeValid || !targetingReady}
            className="rounded-md border border-ap-primary bg-ap-panel px-3 py-1.5 text-sm font-medium text-ap-primary hover:bg-ap-primary/5 disabled:opacity-60"
          >
            {create.isPending ? t("create.saving") : t("create.startFromScratch")}
          </button>
          <button
            type="submit"
            disabled={create.isPending || !codeValid || !yamlBody.trim() || !targetingReady}
            className="rounded-md bg-ap-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-ap-primary/90 disabled:opacity-60"
          >
            {create.isPending ? t("create.saving") : t("create.save")}
          </button>
        </footer>
      </form>
    </Page>
  );
}
